import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text, TouchableRipple } from 'react-native-paper';

import { DownIcon } from '@/assets';
import { useGetSelectedGeoAreas } from '@/features/api/hooks/participant-geo-area/useGetSelectedGeoAreas';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { Row } from '@/shared/components/layout/Row';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    homeArea: {
      color: theme.colors.onPrimary,
      flexShrink: 1
    },
    geoAreas: { color: theme.colors.onPrimary },
    button: {
      alignSelf: 'flex-start',
      flexShrink: 1,
      borderRadius: 8,
      padding: 4
    },
    contentRow: {
      alignItems: 'center',
      gap: 4
    },
    downIcon: {
      alignSelf: 'center'
    },
    textContainer: {
      flexShrink: 1
    },
    nameRow: {
      flexShrink: 1
    }
  });

type Props = {
  name?: string;
  homeGeoAreaId?: string;
  onPress: () => void;
};

export const HomeGeoAreaTitle: FC<Props> = ({
  name,
  homeGeoAreaId,
  onPress
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { isFeatureEnabled } = useFeature();
  const isGeoAreaSelectionEnabled = isFeatureEnabled(
    FeaturesEnum.GeoAreaSelection
  );

  const { data: selectedGeoAreas } = useGetSelectedGeoAreas();

  const { t } = useTranslation(LocalizationNameSpace.START, {
    keyPrefix: 'screens.start.sections.header'
  });

  const nearGeoAreas = useMemo(
    () =>
      selectedGeoAreas?.filter(
        ({ id, leaf }) => leaf === true && id !== homeGeoAreaId
      ) ?? [],
    [selectedGeoAreas, homeGeoAreaId]
  );

  return (
    <TouchableRipple
      onPress={onPress}
      accessibilityRole="button"
      rippleColor={theme.rippleColors.onPrimary}
      style={styles.button}
    >
      <Row style={styles.contentRow}>
        <View style={styles.textContainer}>
          <Row style={styles.nameRow}>
            <Text
              variant="headlineLarge"
              style={styles.homeArea}
              ellipsizeMode="tail"
              numberOfLines={2}
            >
              {name}
            </Text>
            {isGeoAreaSelectionEnabled && (
              <DownIcon
                style={styles.downIcon}
                fill={theme.colors.onPrimary}
                width={28}
                height={28}
              />
            )}
          </Row>
          {hasItems(nearGeoAreas) && (
            <Text
              variant="titleMedium"
              style={styles.geoAreas}
              ellipsizeMode="tail"
              numberOfLines={1}
            >
              {t('subscribedGeoAreas', { count: nearGeoAreas.length })}
            </Text>
          )}
        </View>
      </Row>
    </TouchableRipple>
  );
};

HomeGeoAreaTitle.displayName = 'HomeGeoAreaTitle';
